import * as React from 'react'
import { Box, Grid, Paper, Stack, Typography } from '@mui/material'
import CustomSwitch from '../restrictions/inputs/custom-switch'
import { CustomerT, UserSettingsTableNameEnum } from 'types'
import { useUpdateSettingsVisibility } from 'services/queries/use-update-settings-visibility'

type SettingsStructure = {
  settings: {
    key: string
    visible: boolean
  }[]
  tableName: UserSettingsTableNameEnum
}

type UserSettingsHandlerProps = {
  customerData?: CustomerT
  userSettingsData?: SettingsStructure[]
  userSettingsLoading: boolean
}

// Map technical table names to human-readable titles
const tableNameLabels: Record<UserSettingsTableNameEnum, string> = {
  credit_check: 'Credit Check',
  company_info: 'Company Info',
  monitor_table: 'Monitor Table',
  credit_check_sidebar: 'Credit Check Sidebar',
  fraud_indicators: 'Fraud Indicators',
}

// Format setting key to human-readable label
const formatKeyLabel = (key: string): string => {
  return key
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

function UserSettingsHandler({
  customerData,
  userSettingsData: data,
  userSettingsLoading: isGettingAll,
}: UserSettingsHandlerProps) {
  const { mutate: updateSettingVisibility, isLoading: isUpdatingSetting } =
    useUpdateSettingsVisibility()
  const [updatingKey, setUpdatingKey] = React.useState('')
  const isLoading = isGettingAll || isUpdatingSetting

  if (!customerData) return null

  const handleUpdateSetting = ({
    tableName,
    key,
    state,
  }: {
    tableName: UserSettingsTableNameEnum
    key: string
    state: boolean
  }) => {
    updateSettingVisibility({
      customerId: customerData.id,
      key,
      tableName,
      state,
    })
  }

  const handleChange = ({
    tableName,
    key,
    state,
  }: {
    tableName: UserSettingsTableNameEnum
    key: string
    state: boolean
  }) => {
    setUpdatingKey(key)
    handleUpdateSetting({
      tableName,
      key,
      state,
    })
  }

  return (
    <Stack spacing={3} sx={{ width: '100%', pt: 2, pb: 3 }}>
      {data?.map((item) => (
        <Paper
          key={item.tableName}
          variant="outlined"
          sx={{
            width: '100%',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 2,
              bgcolor: 'action.hover',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              {tableNameLabels[item.tableName] || item.tableName}
            </Typography>
          </Box>

          <Box sx={{ p: 3 }}>
            <Grid container spacing={1}>
              {item.settings.map(({ key, visible }) => (
                <Grid
                  key={key}
                  item
                  xs={6}
                  sm={4}
                  md={3}
                >
                  <CustomSwitch
                    isLoading={updatingKey === key && isLoading}
                    onChange={() =>
                      handleChange({
                        tableName: item.tableName,
                        key,
                        state: !visible,
                      })
                    }
                    value={visible}
                    label={formatKeyLabel(key)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Paper>
      ))}
    </Stack>
  )
}

export default UserSettingsHandler
